import { OrderTicketDetailEntity, TradeTicketDetailEntity } from "@goodfill/domain-entities";
import { asAmount, asBondPriceNominal, Currency, Money } from "@goodfill/domain-properties";
import {
  CouponPaymentComponent,
  YieldComponent,
  MakeWholeCallComponent,
  FaceAmountComponent,
  TimeInForceComponent,
  SettlementValueComponent,
  BondPriceComponent,
  MaturityDateComponent,
  TransactionCostAmountComponent,
  TransactionRateUpfrontComponent,
  AccruedInterestAmountComponent,
  SettlementDateComponent,
  TradeDateComponent,
} from "@goodfill/domain-properties-components";
import { resolveTimeInForce } from "@goodfill/models-typescript";
import { Flex } from "@goodfill/ui-components";
import { useRecoilValue } from "recoil";
import { userDataState } from "../../../../state";
import { Info } from "../../../Info";
import {
  calculateGoodfillTransactionCommissionForAmount,
  calculateYield,
  includeGoodfillCommissionWithBasePrice,
} from "@goodfill/domain-math";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";
import { PricedBond } from "../../../bonds/PricedBond";

interface Props {
  tradeTicket: TradeTicketDetailEntity;
  orderTicket: OrderTicketDetailEntity;
  bond: PricedBond;
}

export const TradeDetail = ({ tradeTicket, orderTicket, bond }: Props) => {
  const userData = useRecoilValue(userDataState);
  const accountId = userData?.account.id;

  const moneyAmount: Money = { amount: tradeTicket.amount, currency: Currency.USD };

  // Todo: those values should be probably part of the trade ticket data
  //       and not to be calculated on the fly
  const tradeValue = asAmount(tradeTicket.amount.mul(tradeTicket.bondPrice));
  const accruedInterestValue = asAmount(tradeTicket.accruedInterest.mul(tradeTicket.amount));
  const settlementValue = asAmount(tradeValue.plus(accruedInterestValue).plus(tradeTicket.finalCommissionAmount));
  const commission = calculateGoodfillTransactionCommissionForAmount(moneyAmount);
  const timeInForce = resolveTimeInForce(orderTicket);
  const _yield = calculateYield(
    TIMEZONE_NEY_YORK,
    bond.couponPayment,
    bond.couponFrequency,
    tradeTicket.bondPrice,
    bond.maturityDate,
    tradeTicket.settlementDate,
  );

  const loadedPrice = includeGoodfillCommissionWithBasePrice(
    tradeTicket.bondPrice,
    moneyAmount,
    orderTicket.direction,
  );

  asBondPriceNominal(tradeTicket.bondPrice.plus(orderTicket.appliedCommission.rate));

  const loadedYield = calculateYield(
    TIMEZONE_NEY_YORK,
    bond.couponPayment,
    bond.couponFrequency,
    loadedPrice,
    bond.maturityDate,
    tradeTicket.settlementDate,
  );

  return (
    <>
      <Flex direction="rowCol" gap={12}>
        <Flex direction="col" gap={2}>
          <Info label="ISIN">{bond.isin}</Info>
          <Info label="Coupon">
            <CouponPaymentComponent value={bond.couponPayment} />
          </Info>
          <Info label="Maturity">
            <MaturityDateComponent value={bond.maturityDate} timezone={TIMEZONE_NEY_YORK} />
          </Info>
          <Info label="Order Price pre-fee">
            <BondPriceComponent value={tradeTicket.bondPrice} />
          </Info>
          <Info label="Order Price post-fee">
            <BondPriceComponent value={loadedPrice} />
          </Info>
          <Info label="Order Yield pre-fee">
            <YieldComponent value={_yield} />
          </Info>
          <Info label="Order Yield post-fee">
            <YieldComponent value={loadedYield} />
          </Info>
          <Info label="S&P">{bond.ratings.sp}</Info>
          <Info label="Moody's">{bond.ratings.moodys}</Info>
        </Flex>
        <Flex direction="col" gap={2}>
          <Info label="Order Direction">{orderTicket.direction}</Info>
          <Info label="Account" valueTextWrap={true}>
            <>{accountId}</>
          </Info>
          <Info label="Make Whole Call">
            {bond.makeWholeCall !== null ? <MakeWholeCallComponent value={bond.makeWholeCall} /> : null}
          </Info>
          <Info label="Payment Rank">{bond.paymentRank}</Info>
          <Info label="Trade Number">{`${tradeTicket.id}`}</Info>
          <Info label="Trace Disclosure">TBD</Info>
        </Flex>
      </Flex>
      <Flex direction="rowCol" gap={12}>
        <Flex direction="col" gap={2}>
          <Info justify="end" label="Face Amount">
            <FaceAmountComponent value={tradeTicket.amount} />
          </Info>
          <Info justify="end" label="Order Execution">
            {" "}
            <TimeInForceComponent value={timeInForce} />{" "}
          </Info>
          <Info justify="end" label="Trade Date">
            <TradeDateComponent timezone={TIMEZONE_NEY_YORK} value={tradeTicket.tradeDate} />
          </Info>
          <Info justify="end" label="Settlement Date">
            <SettlementDateComponent timezone={TIMEZONE_NEY_YORK} value={tradeTicket.settlementDate} />
          </Info>
          <Info justify="end" label="Accrued Interest">
            <AccruedInterestAmountComponent value={accruedInterestValue} />
          </Info>
          <Info justify="end" label="Settlement Value">
            <SettlementValueComponent value={settlementValue} />
          </Info>
        </Flex>
        <Flex direction="col" gap={2} justify="end">
          <Info justify="end" label="Transaction cost Amount">
            <TransactionCostAmountComponent value={commission.commissionAmount.amount} />
          </Info>
          <Info justify="end" label="Transaction cost rate upfront">
            <TransactionRateUpfrontComponent value={commission.commissionRate} />
          </Info>
        </Flex>
      </Flex>
    </>
  );
};
